// Usuários: os MEMBROS da organização ativa (/v1/users no back). Cadastro,
// convite, troca de papel, senha inicial e o vínculo com o owner do HubSpot.
//
// Fronteira com src/api/roles.ts: aqui está QUEM é membro e com que papel;
// lá está O QUE cada papel pode (catálogo de permissões, papéis por marca).
// Os dois falam de "papel", mas de coisas diferentes: `OrgRole` abaixo é o
// papel-base fixo (director|manager|seller) que vem em `OrgUser.role`, e
// `CustomRole` (roles.ts) é o registro por marca com id e permissões. Ficam
// separados porque são features separadas no back — e nenhum dos dois módulos
// importa o outro.
import { api, toApiError } from "./client.js";

// Espelha a feature `users` do back (/v1/users). "Usuário" aqui = membro da org.

// Papéis de uma organização do LAI-Disk.
export const ORG_ROLES = ["director", "manager", "seller"] as const;
export type OrgRole = (typeof ORG_ROLES)[number];

// Rótulos em português para exibição (a UI é toda em pt-br).
export const ROLE_LABELS: Record<OrgRole, string> = {
  director: "Diretor",
  manager: "Gerente",
  seller: "Operador",
};

// Linha de membro retornada por GET /v1/users. `joinedAt` chega como string ISO.
export interface OrgUser {
  memberId: string;
  userId: string;
  name: string;
  email: string;
  image: string | null;
  role: string;
  // Papel EFETIVO do membro, já resolvido no back: id/nome da role custom
  // atribuída (member.role_id), ou da role de sistema da marca com o mesmo
  // baseRole quando role_id é nulo ou aponta para uma role apagada. Nunca
  // vem null fora dessa janela — o front não precisa (nem deve) adivinhar.
  roleId: string | null;
  roleName: string | null;
  joinedAt: string;
  // Deep-link pronto para a página do usuário (owner) no HubSpot, montado no back
  // a partir do portal (env HUBSPOT_PORTAL_ID) + seller_external_id. Vem null
  // quando não há portal configurado ou o usuário não tem owner id no HubSpot —
  // nesse caso o botão "Abrir no HubSpot" não aparece. O front nunca conhece o
  // formato da URL nem o portal.
  hubspotUrl: string | null;
  // "Este vendedor já está ligado a um owner do HubSpot?". O id em si não sai da
  // API — só a resposta. É por ele que a ficha diz se falta preencher: o
  // `hubspotUrl` não serve para isso, porque também some quando o servidor está
  // sem HUBSPOT_PORTAL_ID (dois motivos diferentes, mesmo sintoma).
  hubspotOwnerConfigured: boolean;
}

export interface OrgUsersResponse {
  members: OrgUser[];
  viewerRole: string;
}

// GET /v1/users — membros da org ativa + papel de quem está vendo.
export async function getOrgUsers(): Promise<OrgUsersResponse> {
  const { data, error } = await api<OrgUsersResponse>("/v1/users");
  if (error) throw toApiError("GET /v1/users", error);
  return data;
}

// POST /v1/users/invite — convida por email com um papel da marca (roleId).
// O back ainda aceita o formato antigo (role: OrgRole) por compatibilidade
// durante a janela de deploy, mas o front passa a mandar roleId.
export async function inviteOrgUser(input: {
  email: string;
  roleId: string;
}): Promise<{ invitationId: string; email: string }> {
  const { data, error } = await api<{ invitationId: string; email: string }>("/v1/users/invite", {
    method: "POST",
    body: input,
  });
  if (error) throw toApiError("POST /v1/users/invite", error);
  return data;
}

// POST /v1/users — cria o usuário direto na org ativa, com senha inicial e
// obrigado a trocá-la no primeiro login. `created` = false quando o email já
// existia e só criamos o vínculo com a organização. Papel vai por roleId (o
// back ainda aceita o literal antigo por compatibilidade de deploy).
//
// `initialPassword` vem do back e é a ÚNICA forma de o front conhecer a senha —
// não há constante com ela em lugar nenhum daqui, de propósito: o bundle é
// servido a qualquer visitante. Vem ausente quando `created` é false (o email já
// existia e a senha dele não foi tocada).
export async function createOrgUser(input: {
  name: string;
  email: string;
  roleId: string;
}): Promise<{
  userId: string;
  memberId: string;
  role: string;
  created: boolean;
  initialPassword?: string;
}> {
  const { data, error } = await api<{
    userId: string;
    memberId: string;
    role: string;
    created: boolean;
    initialPassword?: string;
  }>("/v1/users", { method: "POST", body: input });
  if (error) throw toApiError("POST /v1/users", error);
  return data;
}

// PATCH /v1/users/:memberId/role — troca o papel de um membro para uma role
// da marca (roleId). O back ainda aceita o literal antigo por compatibilidade
// de deploy.
export async function updateOrgUserRole(memberId: string, roleId: string): Promise<void> {
  const { error } = await api(`/v1/users/${memberId}/role`, {
    method: "PATCH",
    body: { roleId },
  });
  if (error) throw toApiError(`PATCH /v1/users/${memberId}/role`, error);
}

// PATCH /v1/users/:memberId/name — renomeia o usuário. Restrito a diretor no
// back (403 para os demais).
export async function updateOrgUserName(memberId: string, name: string): Promise<void> {
  const { error } = await api(`/v1/users/${memberId}/name`, {
    method: "PATCH",
    body: { name },
  });
  if (error) throw toApiError(`PATCH /v1/users/${memberId}/name`, error);
}

// PATCH /v1/users/:memberId/hubspot-owner — liga o vendedor ao owner do HubSpot
// (ou desfaz, com null). O back tenta preencher sozinho pelo email no cadastro;
// isto é o caminho manual, para quem o portal não conhecia na hora.
export async function updateOrgUserHubspotOwner(
  memberId: string,
  hubspotOwnerId: string | null,
): Promise<void> {
  const { error } = await api(`/v1/users/${memberId}/hubspot-owner`, {
    method: "PATCH",
    body: { hubspotOwnerId },
  });
  if (error) throw toApiError(`PATCH /v1/users/${memberId}/hubspot-owner`, error);
}

// DELETE /v1/users/:memberId — soft delete do membro na organização.
export async function removeOrgUser(memberId: string): Promise<void> {
  const { error } = await api(`/v1/users/${memberId}`, { method: "DELETE" });
  if (error) throw toApiError(`DELETE /v1/users/${memberId}`, error);
}

// POST /v1/users/:memberId/reset-password — redefine a senha do membro, com
// troca obrigatória no próximo login. Devolve a senha em `initialPassword` pelo
// mesmo motivo do create: é daqui que o gestor a lê para repassar.
export async function resetOrgUserPassword(memberId: string): Promise<{ initialPassword: string }> {
  const { data, error } = await api<{ reset: true; initialPassword: string }>(
    `/v1/users/${memberId}/reset-password`,
    { method: "POST" },
  );
  if (error) throw toApiError(`POST /v1/users/${memberId}/reset-password`, error);
  return { initialPassword: data.initialPassword };
}
